import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet, apiPost } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import { Users, Check, X, Clock, Send, Loader2, AlertTriangle } from 'lucide-react';

const ElitesList = () => {
  const { can } = usePermissions();
  const [allElites, setAllElites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [showActionConfirmModal, setShowActionConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState({ username: '', action: '' });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Pagination state (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter state
  const [filters, setFilters] = useState({ username: '', status: '' });

  const fetchElites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/admin/elites/list?page=1&pageSize=10000`);
      if (response.data.success) {
        let elitesArray = [];
        if (response.data.data && response.data.data.data) {
          elitesArray = response.data.data.data || [];
        } else if (response.data.data && Array.isArray(response.data.data)) {
          elitesArray = response.data.data;
        } else if (Array.isArray(response.data)) {
          elitesArray = response.data;
        } else if (response.data.elites && Array.isArray(response.data.elites)) {
          elitesArray = response.data.elites;
        }
        setAllElites(elitesArray);
      } else {
        setError('Failed to fetch elites data');
      }
    } catch (err) {
      console.error('Error fetching elites:', err);
      setError('Failed to load elites data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchElites();
  }, [fetchElites]);

  // Client-side filtering
  const filteredElites = useMemo(() => {
    const q = filters.username.trim().toLowerCase();
    const statusFilter = filters.status.toLowerCase();
    return allElites.filter((e) => {
      if (q) {
        const hay = `${e.username || ''} ${e.name || ''} ${e.surname || ''} ${e.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter && (e.status || '').toLowerCase() !== statusFilter) return false;
      return true;
    });
  }, [allElites, filters]);

  // Client-side pagination
  const totalRows = filteredElites.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const elites = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredElites.slice(start, start + pageSize);
  }, [filteredElites, safePage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleAction = async (username, action) => {
    setSelectedAction({ username, action });
    setShowActionConfirmModal(true);
  };

  const confirmAction = async () => {
    try {
      setActionLoading(prev => ({ ...prev, [selectedAction.username]: true }));
      const response = await apiPost(`/api/admin/elites/${selectedAction.action}`, { username: selectedAction.username });
      if (response.data.success) {
        const newStatus = response.data.status || (selectedAction.action === 'approve' ? 'Approved' : 'Rejected');
        setAllElites(prev => prev.map(elite =>
          elite.username === selectedAction.username
            ? { ...elite, status: newStatus }
            : elite
        ));
        setShowActionConfirmModal(false);
      } else {
        alert(`Failed to ${selectedAction.action} user: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(`Error ${selectedAction.action}ing user:`, err);
      alert(`Failed to ${selectedAction.action} user. Please try again.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedAction.username]: false }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSendInvite = async () => {
    const username = inviteUsername.trim();
    if (!username) {
      alert('Please enter a username');
      return;
    }
    try {
      setInviteLoading(true);
      const response = await apiPost('/api/admin/elites/invite', { username });
      if (response.data.success) {
        alert(response.data.message || `Invite sent to ${username}`);
        setShowInviteModal(false);
        setInviteUsername('');
        fetchElites();
      } else {
        alert(`Failed to send invite: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending invite:', err);
      alert(err?.response?.data?.message || 'Failed to send invite. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({ username: '', status: '' });
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Approved</span>;
      case 'rejected':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/10 text-red-400 border border-red-500/20">Rejected</span>;
      case 'pending':
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">Pending</span>;
      default:
        return <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-white/[0.05] text-muted-foreground border border-border/40">{status || 'Unknown'}</span>;
    }
  };

  const inputCls = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const labelCls = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-ginox-green mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading elites data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-2">Error</h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button
            onClick={fetchElites}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Services
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
            Elites <span className="text-gradient-ginox">Management</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage elite user applications and approvals
          </p>
        </div>
        {can('POST:/api/admin/elites/invite') && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            <Send className="h-3.5 w-3.5" />
            Send Invite
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-white/[0.05] p-2">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.06em]">Total Elites</p>
              <p className="text-2xl font-semibold text-foreground">{allElites.length}</p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-emerald-500/10 p-2">
              <Check className="h-4 w-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.06em]">Approved</p>
              <p className="text-2xl font-semibold text-foreground">
                {allElites.filter(e => e.status?.toLowerCase() === 'approved').length}
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border/70 bg-card p-5">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-yellow-500/10 p-2">
              <Clock className="h-4 w-4 text-yellow-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-[0.06em]">Pending</p>
              <p className="text-2xl font-semibold text-foreground">
                {allElites.filter(e => e.status?.toLowerCase() === 'pending').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Username</label>
            <input
              type="text"
              value={filters.username}
              onChange={(e) => handleFilterChange('username', e.target.value)}
              placeholder="Search by username..."
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={inputCls}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              className={inputCls}
            >
              <option value={10}>10 per page</option>
              <option value={15}>15 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Elites Table */}
      <div className="overflow-x-auto">
        <div className="px-5 py-3.5 border-b border-border/40 flex justify-between items-center">
          <h2 className="text-xs font-semibold uppercase tracking-[1px] text-muted-foreground">Elites List</h2>
          <span className="text-xs text-muted-foreground">
            Showing {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, totalRows)} of {totalRows}
          </span>
        </div>

        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {['User', 'Status', 'Date', 'Actions'].map((h) => (
                <th key={h} className="border-b border-border/70 px-5 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!Array.isArray(elites) || elites.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-sm text-muted-foreground">
                  {!Array.isArray(elites) ? 'Invalid data format' : 'No elites found'}
                </td>
              </tr>
            ) : (
              elites.map((elite, index) => (
                <tr key={elite._id || index} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-white/[0.05] border border-border/40 flex items-center justify-center flex-shrink-0">
                        <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="text-sm font-mono text-foreground">{elite.username || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">{elite.name} {elite.surname}</div>
                        <div className="text-xs text-muted-foreground/60">{elite.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    {getStatusBadge(elite.status)}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-muted-foreground font-mono">
                    {elite.createdAt ? new Date(elite.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <div className="flex gap-2">
                      {elite.status?.toLowerCase() === 'pending' && (
                        <>
                          {can('POST:/api/admin/elites/approve') && (
                            <button
                              onClick={() => handleAction(elite.username, 'approve')}
                              disabled={actionLoading[elite.username]}
                              className="inline-flex items-center gap-1 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
                            >
                              {actionLoading[elite.username] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Check className="h-3 w-3" />
                              )}
                              Approve
                            </button>
                          )}
                          {can('POST:/api/admin/elites/reject') && (
                            <button
                              onClick={() => handleAction(elite.username, 'reject')}
                              disabled={actionLoading[elite.username]}
                              className="inline-flex items-center gap-1 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                            >
                              {actionLoading[elite.username] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                              Reject
                            </button>
                          )}
                        </>
                      )}
                      {elite.status?.toLowerCase() !== 'pending' && (
                        <span className="text-xs text-muted-foreground">
                          {elite.status?.toLowerCase() === 'approved' ? 'Already Approved' : 'Already Rejected'}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={totalRows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Action Confirmation Modal */}
      <ConfirmationModal
        isOpen={showActionConfirmModal}
        onClose={() => setShowActionConfirmModal(false)}
        onConfirm={confirmAction}
        title={`${selectedAction.action === 'approve' ? 'Approve' : 'Reject'} Elite User`}
        message={`Are you sure you want to ${selectedAction.action} the elite user ${selectedAction.username}?`}
        confirmText={selectedAction.action === 'approve' ? 'Approve' : 'Reject'}
        loading={actionLoading[selectedAction.username]}
        loadingText={`${selectedAction.action === 'approve' ? 'Approving' : 'Rejecting'}...`}
        danger={selectedAction.action === 'reject'}
      />

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="rounded-xl border border-border/70 bg-card p-6 shadow-xl w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <Send className="h-4 w-4 text-ginox-green" />
              <h3 className="text-sm font-semibold text-foreground">Send Elite Invite</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Enter the username of the user you want to invite as an elite.
            </p>
            <label className={labelCls}>Username</label>
            <input
              type="text"
              value={inviteUsername}
              onChange={(e) => setInviteUsername(e.target.value)}
              placeholder="e.g. 311488"
              disabled={inviteLoading}
              className={inputCls + " mb-6 disabled:opacity-50"}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !inviteLoading) handleSendInvite();
              }}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowInviteModal(false); setInviteUsername(''); }}
                disabled={inviteLoading}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={inviteLoading || !inviteUsername.trim()}
                className="inline-flex items-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
              >
                {inviteLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-3 w-3" />
                    Send Invite
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElitesList;
